'use-client';

import { Select } from '@pickleballinc/react-ui';
import { Controller, useFormContext } from 'react-hook-form';

export interface Options {
	value: string;
	label: React.ReactNode;
	renderLabel?: React.ReactNode;
}

interface SelectFormProps {
	name: string;
	options: Options[];
	label: string;
	placeholder?: string;
	className?: string;
	labelClassName?: string;
	sendAsNumber?: boolean;
	considerWatchWhenDisplayingError?: boolean;
	isSearchable?: boolean;
	isDisabled?: boolean;
	setSelectedItem?: (item: any) => void;
	noOptionsMessage?: string;
	errorMessage?: string;
	deps?: string;
}

export default function SelectForm({
	name,
	placeholder,
	options,
	label,
	className,
	labelClassName,
	sendAsNumber = true,
	considerWatchWhenDisplayingError = true,
	isSearchable = true,
	isDisabled = false,
	setSelectedItem,
	noOptionsMessage = 'No Options',
	errorMessage = undefined,
	deps = undefined
}: SelectFormProps) {
	const { control, watch } = useFormContext();
	return (
		<Controller
			control={control}
			name={name}
			{...(deps && { rules: { deps: deps } })}
			render={({ field: { value, onChange }, fieldState }) => {
				const doesValueExists = value !== null && value !== undefined;
				return (
					<div className="flex flex-col gap-1">
						<span className={`text-sm ${labelClassName}`}>{label}</span>
						<Select
							selectTheme="orange"
							id={name}
							placeholder={placeholder}
							options={options}
							value={doesValueExists ? options.find((option) => option.value === value.toString()) : undefined}
							onChange={(e: any) => {
								onChange(sendAsNumber ? Number(e.value) : e.value);
								if (setSelectedItem) setSelectedItem(e.value);
							}}
							className={`${
								fieldState.error && (considerWatchWhenDisplayingError ? !watch(name) : true) ? 'rounded border border-error-300' : ''
							} ${className}`}
							noSeparator
							menuPlacement="auto"
							isSearchable={isSearchable}
							isDisabled={isDisabled}
							noOptionsMessage={() => noOptionsMessage}
							getOptionLabel={(option: any) => option.label}
							formatOptionLabel={(option: any) => option.renderLabel ?? option.label}
							menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
							menuPosition="fixed"
						/>
						{fieldState.error && (considerWatchWhenDisplayingError ? !watch(name) : true) ? (
							<span className="text-sm text-error-500">{errorMessage ?? `${label} is required`}</span>
						) : null}
					</div>
				);
			}}
		/>
	);
}
